import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const BookVideoTrailer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Book Trailer Production',
      description: 'Professional cinematic book trailers that capture your story\'s essence and drive book sales.',
      features: ['30-60 second videos', 'Custom cinematography', 'Professional voiceover', 'Music licensing']
    },
    {
      title: 'Social Media Videos',
      description: 'Short-form video content optimized for Instagram, TikTok, Facebook, and YouTube promotion.',
      features: ['15-30 second clips', 'Platform optimization', 'Multiple formats', 'Eye-catching hooks']
    },
    {
      title: 'Author Interview Videos',
      description: 'Professional interview recordings discussing your book, writing process, and author brand.',
      features: ['Professional filming', 'Interview coaching', 'Multi-camera setup', 'Post-production editing']
    },
    {
      title: 'Animated Book Trailers',
      description: '2D/3D animated trailers perfect for children\'s books, fantasy, and sci-fi genres.',
      features: ['Custom animation', 'Character design', 'Motion graphics', 'Sound design']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Creative Brief & Concept',
      description: 'We discuss your book\'s themes, target audience, and marketing goals. Our creative team develops trailer concepts that capture your story\'s essence.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'Production & Filming',
      description: 'Professional video production with cinematography, voiceover recording, and visual effects. We handle all technical aspects of creating your trailer.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Editing & Delivery',
      description: 'Final editing with music, sound effects, and motion graphics. You receive files optimized for YouTube, social media, and your website.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png',
      title: 'Book Marketing Expertise',
      description: 'We understand book marketing and create trailers that convert viewers into readers.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Professional Quality',
      description: 'Broadcast-quality video production with professional equipment and editing.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Fast Turnaround',
      description: 'Most book trailers completed within 2-3 weeks from concept to final delivery.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Unlimited Revisions',
      description: 'We refine until you\'re thrilled with your book trailer.'
    }
  ];

  const faqs = [
    { question: 'How much does a book video trailer cost?', answer: 'Book trailer pricing ranges from $500-3,000 depending on complexity. Basic trailers with stock footage and text: $500-1,000. Professional trailers with custom filming and voiceover: $1,500-2,500. Animated trailers: $2,000-3,000+. We provide detailed quotes based on your specific needs.' },
    { question: 'How long should my book trailer be?', answer: '30-60 seconds is ideal. This length is perfect for capturing attention without losing viewers. Shorter 15-30 second versions work well for social media ads. Longer 90-second trailers can work for complex stories but risk viewer drop-off.' },
    { question: 'Do book trailers actually sell books?', answer: 'Yes, when done well. Book trailers increase book awareness by 40-60% and can boost sales by 20-30% according to industry studies. They\'re particularly effective on YouTube, Facebook, and as website homepage features. The key is professional quality and strategic promotion.' },
    { question: 'Can you use footage from my book cover or illustrations?', answer: 'Absolutely! We can incorporate your book cover, interior illustrations, author photos, and any other visual assets you own. Combining your existing art with motion graphics and cinematography creates a cohesive brand presentation.' },
    { question: 'What about music licensing?', answer: 'All music in your trailer is properly licensed for commercial use. We source music from professional libraries and handle all licensing. You receive full commercial rights to use your trailer anywhere - YouTube, social media, your website, paid advertising.' },
    { question: 'Will you provide different versions for different platforms?', answer: 'Yes! We deliver your trailer in multiple formats: horizontal (16:9) for YouTube and websites, square (1:1) for Instagram feed, and vertical (9:16) for Instagram Stories/Reels and TikTok. Each optimized for its platform.' },
    { question: 'Can I see examples before committing?', answer: 'Yes, we provide a portfolio of book trailers we\'ve created across various genres. During the creative brief, we\'ll show you trailers similar to what we envision for your book so you know exactly what to expect.' },
    { question: 'How involved will I be in the production process?', answer: 'As involved as you want. We provide creative concepts for approval, share rough cuts for feedback, and make revisions based on your input. Most authors prefer to approve major milestones while trusting our creative expertise on execution details.' }
  ];

  const testimonials = [
    { name: 'Jennifer Brooks', title: 'YA Fantasy Author', text: 'The book trailer exceeded my expectations! It perfectly captured the magical atmosphere of my novel. Shares and engagement on social media tripled.', rating: 5 },
    { name: 'Michael Torres', title: 'Thriller Author', text: 'The cinematic quality is outstanding. The trailer has been my most effective marketing tool. It\'s on my website, social media, and I use it for paid ads.', rating: 5 },
    { name: 'Lisa Chen', title: 'Romance Author', text: 'The animated trailer for my rom-com series is adorable and perfectly captures the tone. Readers say they bought the book because of the trailer!', rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL BOOK VIDEO TRAILERS</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Cinematic Book Trailers That <span className="text-amber-700">Sell Books</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional video trailers that capture your story's essence and turn viewers into readers. Perfect for social media, your website, and advertising campaigns.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button onClick={() => setIsModalOpen(true)} className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                  Get Trailer Quote
                </Button>
                <Button onClick={() => scrollToSection('process')} className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                  See How It Works
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div><div className="text-4xl font-bold text-amber-700 mb-2">500+</div><div className="text-gray-600 text-sm">Trailers Created</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">30-60s</div><div className="text-gray-600 text-sm">Optimal Length</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">40%+</div><div className="text-gray-600 text-sm">Awareness Increase</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">2-3</div><div className="text-gray-600 text-sm">Weeks Average</div></div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <img src="https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&h=600&fit=crop" alt="Book video production" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" alt="Trailers" className="w-full h-full object-contain" />
                    </div>
                    <div><div className="text-2xl font-bold text-gray-900">500+</div><div className="text-sm text-gray-600">Trailers Created</div></div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Complete <span className="text-amber-700">Video Services</span></h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From cinematic book trailers to social media content that drives engagement.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How It <span className="text-amber-700">Works</span></h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>
                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Choose <span className="text-amber-700">Rushmore Publishing</span></h2>
              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&h=600&fit=crop" alt="Video production" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&h=800&fit=crop" alt="Create trailer" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">FREE CREATIVE CONSULTATION</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Create Your Book Trailer?</h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">Get a free quote and creative consultation. Let's turn your book into a compelling video that drives sales.</p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all">
              Get Your Free Quote
            </Button>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20 mt-12">
              <div><div className="text-3xl font-bold mb-2">500+</div><div className="text-sm opacity-80">Trailers Created</div></div>
              <div><div className="text-3xl font-bold mb-2">30-60s</div><div className="text-sm opacity-80">Optimal Length</div></div>
              <div><div className="text-3xl font-bold mb-2">40%+</div><div className="text-sm opacity-80">Awareness Boost</div></div>
              <div><div className="text-3xl font-bold mb-2">24/7</div><div className="text-sm opacity-80">Support</div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our <span className="text-amber-700">Authors Say</span></h2></div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">{[...Array(testimonial.rating)].map((_, i) => (<span key={i} className="text-amber-500 text-xl">★</span>))}</div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div><div className="font-bold text-gray-900">{testimonial.name}</div><div className="text-sm text-gray-600">{testimonial.title}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked <span className="text-amber-700">Questions</span></h2></div>
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all">
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" /> : <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />}
                </button>
                {openFaq === index && (<div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookVideoTrailer;